import { useEffect, useRef, useState, type CSSProperties } from "react";
import { toast } from "react-hot-toast";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { E2EAgentConflictError } from "../../api/transport";
import { useAuth } from "../../auth/AuthContext";
import { hideChatAndFastTrack } from "../../lib/featureFlags";
import {
  Empty,
  ErrorState,
  InlineNotice,
  Loading,
  PageHeader,
} from "../../components/WorkspacePrimitives";
import {
  ChatIcon,
  Button,
  ConfirmDialog,
  IconButton,
  IterateIcon,
  Modal,
  ModalCloseButton,
} from "../../components/ui";
import {
  applyOptimisticXLMStructuralEdits,
  useAgentCompanies,
  useAgentConfiguration,
  useAgentDocuments,
  useAgentModel,
  useAgentModelVersions,
  useCreateXLMRevision,
  useRestoreXLMRevision,
  useXLMModel,
  useXLMRevisions,
  type XLMEditCellOperation,
  type XLMEditOperation,
  type XLMRevisionSummary,
  type XLMWorkbookSnapshot,
} from "../api/e2e";
import AgentArtifactActions from "../components/AgentArtifactActions";
import AgentAssumptionsReviewPanel from "../components/AgentAssumptionsReviewPanel";
import AgentGuidanceReviewPanel from "../components/AgentGuidanceReviewPanel";
import AgentModelDocumentsPanel from "../components/AgentModelDocumentsPanel";
import AgentModelChatSidePeek, {
  DEFAULT_MODEL_CHAT_WIDTH,
} from "../components/AgentModelChatSidePeek";
import AgentModelTabs, {
  agentModelTab,
  type AgentModelTab,
} from "../components/AgentModelTabs";
import AgentRunDetails from "../components/AgentRunDetails";
import AgentSummaryPanel from "../components/AgentSummaryPanel";
import AgentVisibilityControl from "../components/AgentVisibilityControl";
import AgentXLMViewer from "../components/AgentXLMViewer";
import AgentXLMRevisionHistory from "../components/AgentXLMRevisionHistory";
import CompanyContextNavigation from "../components/CompanyContextNavigation";
import { useXLMEditQueue } from "../hooks/useXLMEditQueue";
import {
  modelChatSidePeekOpen,
  modelChatSidePeekParams,
} from "../lib/modelChat";
import {
  clearModelReferenceParams,
  MODEL_REFERENCE_INSPECT_PARAM,
  MODEL_REFERENCE_PARAM,
  modelReferenceAvailable,
  openModelReferenceParams,
  parseModelReference,
  type ModelReference,
} from "../lib/modelReferences";
import { XLM_REVIEW_RECORD_PARAM, type XLMReviewTab } from "../lib/xlmReviews";
import AgentLaunchPage from "./AgentLaunchPage";

const DIRTY_XLM_EDITOR_MESSAGE =
  "Save or reset this cell before leaving the model editor.";
const PENDING_XLM_EDITOR_MESSAGE =
  "Wait for model edits to finish saving before using this revision.";
function isRowMoveOperation(operation: XLMEditOperation): boolean {
  return (
    operation.kind === "move_node" || operation.kind === "move_breakdown_item"
  );
}

export default function AgentModelDetailPage() {
  const auth = useAuth();
  const restrictedProductionUi = hideChatAndFastTrack();
  const { companyId = "", versionId: routeVersionId = "" } = useParams<{
    companyId: string;
    versionId?: string;
  }>();
  const [params, setParams] = useSearchParams();
  const [iterating, setIterating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [xlmEditorDirty, setXlmEditorDirty] = useState(false);
  const [modelChatWidth, setModelChatWidth] = useState(
    DEFAULT_MODEL_CHAT_WIDTH,
  );
  const [modelChatFullScreen, setModelChatFullScreen] = useState(false);
  const [chatEditActive, setChatEditActive] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<XLMRevisionSummary | null>(
    null,
  );
  const [pendingRowMoveSaveCount, setPendingRowMoveSaveCount] = useState(0);
  const activeTab = agentModelTab(params.get("tab"));
  const companies = useAgentCompanies();
  const configuration = useAgentConfiguration();
  const versions = useAgentModelVersions(companyId);
  const requestedVersion = params.get("version") ?? "";
  const versionId =
    routeVersionId || requestedVersion || versions.data?.[0]?.id || "";
  useEffect(() => setChatEditActive(false), [versionId]);
  const editQueue = useXLMEditQueue(companyId, versionId);
  const createRevision = useCreateXLMRevision();
  const restoreRevision = useRestoreXLMRevision();
  const structuralUndoTargets = useRef<XLMWorkbookSnapshot[]>([]);
  const rowMoveSaveTail = useRef<Promise<void>>(Promise.resolve());
  const workbookRef = useRef<XLMWorkbookSnapshot | null>(null);
  const revisionMutationPending =
    createRevision.isPending || restoreRevision.isPending;
  const modelSavePending =
    editQueue.hasPending ||
    revisionMutationPending ||
    pendingRowMoveSaveCount > 0;
  const modelMutationLocked = modelSavePending || chatEditActive;
  const xlmEditorBlocked = xlmEditorDirty || modelSavePending;
  const detail = useAgentModel(companyId, versionId);
  const xlm = useXLMModel(companyId, versionId, !xlmEditorBlocked);
  const revisions = useXLMRevisions(
    companyId,
    versionId,
    activeTab === "model" && !modelSavePending,
  );
  const documents = useAgentDocuments(companyId, {
    runId: detail.data?.run_id ?? "",
  });
  useEffect(() => {
    structuralUndoTargets.current = [];
    rowMoveSaveTail.current = Promise.resolve();
    setPendingRowMoveSaveCount(0);
  }, [versionId]);

  useEffect(() => {
    if (restrictedProductionUi) {
      if (!modelChatSidePeekOpen(params)) return;
      setParams(modelChatSidePeekParams(params, false), { replace: true });
      return;
    }
    if (params.get("tab") !== "chat") return;
    setParams(modelChatSidePeekParams(params, true), { replace: true });
  }, [params, restrictedProductionUi, setParams]);

  useEffect(() => {
    if (!xlmEditorBlocked) return;
    const blockLinkNavigation = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const target =
        event.target instanceof Element
          ? event.target.closest<HTMLAnchorElement>("a[href]")
          : null;
      if (
        !target ||
        target.target === "_blank" ||
        target.hasAttribute("download")
      ) {
        return;
      }
      const destination = new URL(target.href, window.location.href);
      if (
        destination.hash !== "" &&
        destination.pathname === window.location.pathname &&
        destination.search === window.location.search
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      toast.error(
        modelSavePending
          ? PENDING_XLM_EDITOR_MESSAGE
          : DIRTY_XLM_EDITOR_MESSAGE,
      );
    };
    const blockBrowserUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    document.addEventListener("click", blockLinkNavigation, true);
    window.addEventListener("beforeunload", blockBrowserUnload);
    return () => {
      document.removeEventListener("click", blockLinkNavigation, true);
      window.removeEventListener("beforeunload", blockBrowserUnload);
    };
  }, [modelSavePending, xlmEditorBlocked]);

  if (versions.isPending && !versions.data)
    return <Loading label="Loading company model versions…" />;
  if (versions.error && !versions.data) {
    return (
      <ErrorState error={versions.error} onRetry={() => versions.refetch()} />
    );
  }
  if (!versions.data || versions.data.length === 0) {
    return (
      <div className="page page--workspace">
        <PageHeader>
          <Link
            to={`/companies/${encodeURIComponent(companyId)}`}
            className="back-link"
          >
            ← Company models
          </Link>
        </PageHeader>
        <Empty>
          No successful model version is available for this company.
        </Empty>
      </div>
    );
  }
  if (detail.isPending && !detail.data) {
    return <Loading label="Opening company model…" />;
  }
  if (detail.error && !detail.data) {
    return <ErrorState error={detail.error} onRetry={() => detail.refetch()} />;
  }
  const model = detail.data;
  const workbook = xlm.data;
  workbookRef.current = workbook ?? null;
  const snapshot = workbook?.model;
  if (!model) return null;
  const currentCompany = companies.data?.find(
    (company) => company.id === companyId,
  );
  const sourceDocuments = documents.data ?? [];
  const modelArtifacts = (model.artifacts ?? []).filter(
    (artifact) => artifact.role === "workbook",
  );
  const chatOpen = !restrictedProductionUi && modelChatSidePeekOpen(params);
  const modelReferenceValue = params.get(MODEL_REFERENCE_PARAM);
  const modelReference = parseModelReference(modelReferenceValue);
  const inspectModelReference =
    params.get(MODEL_REFERENCE_INSPECT_PARAM) === "true";
  const activeReviewRecordId = params.get(XLM_REVIEW_RECORD_PARAM);
  const unavailableModelReference =
    modelReferenceValue !== null &&
    snapshot !== undefined &&
    (modelReference === null ||
      !modelReferenceAvailable(snapshot, modelReference));

  const clearModelReference = () => {
    if (!params.has(MODEL_REFERENCE_PARAM)) return;
    setParams(clearModelReferenceParams(params), { replace: true });
  };

  const openModelReference = (reference: ModelReference, inspect = false) => {
    const next = openModelReferenceParams(params, reference, { inspect });
    next.delete(XLM_REVIEW_RECORD_PARAM);
    setParams(modelChatSidePeekParams(next, chatOpen));
  };

  const setChatOpen = (open: boolean) => {
    if (restrictedProductionUi) return;
    if (!open) setModelChatFullScreen(false);
    setParams(modelChatSidePeekParams(params, open));
  };

  const allowLeavingXLMEditor = (): boolean => {
    if (!xlmEditorBlocked) return true;
    toast.error(
      modelSavePending ? PENDING_XLM_EDITOR_MESSAGE : DIRTY_XLM_EDITOR_MESSAGE,
    );
    return false;
  };

  const openReview = (tab: XLMReviewTab, recordId: string) => {
    if (!allowLeavingXLMEditor()) return;
    const next = modelChatSidePeekParams(params, chatOpen);
    next.set("tab", tab);
    next.set(XLM_REVIEW_RECORD_PARAM, recordId);
    next.delete(MODEL_REFERENCE_PARAM);
    next.delete(MODEL_REFERENCE_INSPECT_PARAM);
    setParams(next);
  };

  const selectTab = (tab: AgentModelTab) => {
    if (tab !== activeTab && !allowLeavingXLMEditor()) return;
    const next = modelChatSidePeekParams(params, chatOpen);
    if (tab === "summary") {
      next.delete("tab");
    } else {
      next.set("tab", tab);
    }
    if (tab !== "model") {
      next.delete(MODEL_REFERENCE_PARAM);
      next.delete(MODEL_REFERENCE_INSPECT_PARAM);
    }
    next.delete(XLM_REVIEW_RECORD_PARAM);
    setParams(next);
  };

  const refreshAfterConflict = async (error: unknown): Promise<boolean> => {
    if (!(error instanceof E2EAgentConflictError)) return false;
    const [refreshed] = await Promise.all([xlm.refetch(), revisions.refetch()]);
    if (refreshed.data) workbookRef.current = refreshed.data;
    toast.error(
      error.currentRevision === null
        ? "The model changed before this save. The current revision was refreshed; review and try again."
        : `The model changed to revision ${error.currentRevision}. The current revision was refreshed; review and try again.`,
    );
    return true;
  };

  const saveCell = (operation: XLMEditCellOperation): boolean => {
    if (chatEditActive) {
      toast.error(
        "Approve or reject the active chat edit before changing the grid.",
      );
      return false;
    }
    if (!workbook) {
      toast.error(
        "The model snapshot is still loading. Try again in a moment.",
      );
      return false;
    }
    const queued = editQueue.enqueue(operation);
    if (!queued) {
      toast.error("This cell edit could not be staged locally.");
    }
    return queued;
  };

  const queueRowMoveOperations = (
    operations: XLMEditOperation[],
  ): Promise<boolean> => {
    setPendingRowMoveSaveCount((current) => current + 1);
    const task = rowMoveSaveTail.current.then(async () => {
      const undoTarget = workbookRef.current;
      if (!undoTarget) return false;
      try {
        const mutation = await createRevision.mutateAsync({
          companyId,
          versionId,
          baseRevision: undoTarget.xlm_snapshot.revision,
          operations,
          idempotencyKey: `xlm-move-${crypto.randomUUID()}`,
        });
        const projected = applyOptimisticXLMStructuralEdits(
          undoTarget,
          operations,
        );
        if (projected) {
          workbookRef.current = {
            ...projected,
            xlm_snapshot: mutation.xlm_snapshot,
          };
        }
        structuralUndoTargets.current.push(undoTarget);
        return true;
      } catch (error) {
        if (await refreshAfterConflict(error)) return false;
        toast.error(
          error instanceof Error ? error.message : "The row move failed.",
        );
        return false;
      }
    });
    rowMoveSaveTail.current = task.then(
      () => undefined,
      () => undefined,
    );
    return task.finally(() => {
      setPendingRowMoveSaveCount((current) => Math.max(0, current - 1));
    });
  };

  const saveOperations = async (
    operations: XLMEditOperation[],
    options?: { optimistic?: boolean },
  ): Promise<boolean> => {
    if (chatEditActive) {
      toast.error(
        "Approve or reject the active chat edit before changing the grid.",
      );
      return false;
    }
    if (!workbook) {
      toast.error(
        "The model snapshot is still loading. Try again in a moment.",
      );
      return false;
    }
    const baseRevision = workbook.xlm_snapshot.revision;
    if (
      operations.every(
        (operation): operation is XLMEditCellOperation =>
          operation.kind === "edit_cell",
      )
    ) {
      const queued = editQueue.enqueueBatch(operations);
      if (!queued) {
        toast.error("The pasted cells could not be staged locally.");
      }
      return queued;
    }
    if (operations.length > 0 && operations.every(isRowMoveOperation)) {
      if (editQueue.hasPending || restoreRevision.isPending) {
        toast.error(PENDING_XLM_EDITOR_MESSAGE);
        return false;
      }
      return queueRowMoveOperations(operations);
    }
    if (modelMutationLocked) {
      toast.error(PENDING_XLM_EDITOR_MESSAGE);
      return false;
    }
    const undoTarget = workbook;
    try {
      await createRevision.mutateAsync({
        companyId,
        versionId,
        baseRevision,
        operations,
        idempotencyKey: `xlm-edit-${crypto.randomUUID()}`,
        optimistic: options?.optimistic,
      });
      structuralUndoTargets.current.push(undoTarget);
      return true;
    } catch (error) {
      if (await refreshAfterConflict(error)) return false;
      toast.error(
        error instanceof Error ? error.message : "The model edit failed.",
      );
      return false;
    }
  };

  const undoStructuralOperation = async (): Promise<boolean> => {
    const undoTarget = structuralUndoTargets.current.at(-1);
    const baseRevision = workbook?.xlm_snapshot.revision;
    if (!undoTarget || baseRevision === undefined || modelMutationLocked) {
      return false;
    }
    try {
      await restoreRevision.mutateAsync({
        companyId,
        versionId,
        revision: undoTarget.xlm_snapshot.revision,
        baseRevision,
        idempotencyKey: `xlm-undo-${crypto.randomUUID()}`,
        optimisticSnapshot: undoTarget,
      });
      if (structuralUndoTargets.current.at(-1) === undoTarget) {
        structuralUndoTargets.current.pop();
      }
      toast.success("Row change undone.");
      return true;
    } catch (error) {
      if (await refreshAfterConflict(error)) return false;
      toast.error(
        error instanceof Error ? error.message : "The row undo failed.",
      );
      return false;
    }
  };

  const confirmRestore = async (): Promise<void> => {
    if (!restoreTarget) return;
    if (chatEditActive) {
      toast.error(
        "Approve or reject the active chat edit before restoring a revision.",
      );
      return;
    }
    const baseRevision = workbook?.xlm_snapshot.revision;
    if (baseRevision === undefined) {
      toast.error(
        "The model snapshot is still loading. Try again in a moment.",
      );
      return;
    }
    try {
      const restored = await restoreRevision.mutateAsync({
        companyId,
        versionId,
        revision: restoreTarget.revision,
        baseRevision,
        idempotencyKey: `xlm-restore-${crypto.randomUUID()}`,
      });
      structuralUndoTargets.current = [];
      setRestoreTarget(null);
      toast.success(
        `Restored revision ${restoreTarget.revision} as revision ${restored.revision}.`,
      );
    } catch (error) {
      if (await refreshAfterConflict(error)) {
        setRestoreTarget(null);
        return;
      }
      toast.error(
        error instanceof Error ? error.message : "Revision restore failed.",
      );
    }
  };

  return (
    <div
      className={[
        "page",
        "page--agent-model",
        chatOpen ? "page--agent-model-chat-open" : "",
        chatOpen && modelChatFullScreen
          ? "page--agent-model-chat-fullscreen"
          : "",
        "page--agent-model-contained",
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        {
          "--agent-model-chat-user-width": `${modelChatWidth}px`,
        } as CSSProperties
      }
    >
      <PageHeader className="model-workspace-header">
        {currentCompany && (
          <CompanyContextNavigation
            companies={companies.data ?? []}
            company={currentCompany}
            backTo={`/companies/${encodeURIComponent(companyId)}`}
            backLabel="Published models"
            onBeforeNavigate={allowLeavingXLMEditor}
            controls={
              <AgentVisibilityControl
                runId={model.run_id}
                visibility={model.visibility}
                ownerSubject={model.generated_by?.subject ?? null}
              />
            }
            actions={
              <>
                {!restrictedProductionUi && (
                  <IconButton
                    tone={chatOpen ? "secondary" : "primary"}
                    size="lg"
                    label={chatOpen ? "Close model chat" : "Ask model"}
                    tooltip={
                      chatOpen
                        ? "Close the model chat side peek."
                        : "Ask questions about this exact published model."
                    }
                    icon={<ChatIcon />}
                    aria-expanded={chatOpen}
                    aria-controls="agent-model-chat-sidepeek"
                    disabled={modelSavePending && !chatOpen}
                    onClick={() => setChatOpen(!chatOpen)}
                  />
                )}
                {configuration.data?.iteration_enabled && (
                  <IconButton
                    tone="primary"
                    size="lg"
                    label={`Iterate on model version ${model.sequence}`}
                    tooltip="Start a new run from this model version and describe what you want to change."
                    icon={<IterateIcon />}
                    disabled={modelMutationLocked}
                    onClick={() => {
                      if (allowLeavingXLMEditor()) setIterating(true);
                    }}
                  />
                )}
                <AgentArtifactActions
                  companyId={companyId}
                  versionId={versionId}
                  runId={model.run_id}
                  artifacts={modelArtifacts}
                  compact
                  compactSize="lg"
                  compactTone="primary"
                  hideSummary
                  disabled={xlmEditorBlocked || chatEditActive}
                />
              </>
            }
          />
        )}
        <AgentModelTabs value={activeTab} onChange={selectTab} />
      </PageHeader>

      {iterating && configuration.data?.iteration_enabled && (
        <Modal
          size="large"
          className="agent-launch-modal"
          ariaLabel={`Iterate on model version ${model.sequence}`}
          onClose={() => setIterating(false)}
        >
          <ModalCloseButton
            floating
            label="Close iteration form"
            onClick={() => setIterating(false)}
          />
          <AgentLaunchPage
            modal
            initialCompanyId={companyId}
            initialBaseModelVersionId={model.id}
            onClose={() => setIterating(false)}
          />
        </Modal>
      )}

      {historyOpen && (
        <AgentXLMRevisionHistory
          companyId={companyId}
          versionId={versionId}
          onClose={() => setHistoryOpen(false)}
          onRequestRestore={(revision) => {
            if (chatEditActive) {
              toast.error(
                "Approve or reject the active chat edit before restoring a revision.",
              );
              return;
            }
            setHistoryOpen(false);
            setRestoreTarget(revision);
          }}
        />
      )}

      {restoreTarget && (
        <ConfirmDialog
          title={`Restore revision ${restoreTarget.revision}?`}
          description={`This creates a new immutable revision from revision ${restoreTarget.revision}. Existing history is preserved.`}
          confirmLabel="Restore revision"
          busy={restoreRevision.isPending}
          onCancel={() => setRestoreTarget(null)}
          onConfirm={() => void confirmRestore()}
        />
      )}

      {(versions.error || detail.error || xlm.error || revisions.error) && (
        <InlineNotice tone="warning">
          Some model context could not refresh. The displayed version remains
          pinned.
        </InlineNotice>
      )}

      {editQueue.status === "failed" && (
        <InlineNotice tone="warning">
          <span>
            {editQueue.error?.message ?? "The model edit could not be saved."}
          </span>{" "}
          <Button
            size="xs"
            tone="secondary"
            disabled={chatEditActive}
            onClick={editQueue.retry}
          >
            Retry save
          </Button>
        </InlineNotice>
      )}

      {editQueue.status === "conflict" && (
        <InlineNotice tone="warning">
          <span>
            Another analyst changed this cell
            {editQueue.conflictRevision === null
              ? "."
              : ` in revision ${editQueue.conflictRevision}.`}
          </span>{" "}
          <Button size="xs" tone="secondary" onClick={editQueue.keepMine}>
            Keep my edit
          </Button>{" "}
          <Button
            size="xs"
            tone="quiet"
            onClick={() => void editQueue.useLatest()}
          >
            Use latest
          </Button>
        </InlineNotice>
      )}

      <div
        id={`agent-model-panel-${activeTab}`}
        className={[
          "agent-model-tab-panel",
          activeTab === "summary"
            ? "agent-model-tab-panel--split-summary"
            : activeTab === "documents" ||
                activeTab === "model" ||
                activeTab === "assumptions" ||
                activeTab === "guidance"
              ? "agent-model-tab-panel--nested-scroll"
              : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="tabpanel"
        aria-labelledby={`agent-model-tab-${activeTab}`}
        tabIndex={0}
      >
        {activeTab === "model" && (
          <>
            {unavailableModelReference && (
              <InlineNotice tone="warning">
                This model reference is unavailable in this model version.
              </InlineNotice>
            )}
            {xlm.isPending && !snapshot ? (
              <Loading label="Loading model…" />
            ) : xlm.error && !snapshot ? (
              <ErrorState error={xlm.error} onRetry={() => xlm.refetch()} />
            ) : snapshot ? (
              <AgentXLMViewer
                key={versionId}
                model={snapshot}
                revision={workbook?.xlm_snapshot.revision}
                saveStatus={modelSavePending ? "saving" : editQueue.status}
                pendingSaveCount={
                  editQueue.pendingCount +
                  pendingRowMoveSaveCount +
                  (revisionMutationPending && pendingRowMoveSaveCount === 0
                    ? 1
                    : 0)
                }
                onSaveCell={saveCell}
                onSaveOperations={saveOperations}
                onUndoStructural={undoStructuralOperation}
                allowQueuedRowMoves={
                  pendingRowMoveSaveCount > 0 &&
                  !editQueue.hasPending &&
                  !restoreRevision.isPending
                }
                pasteLocked={
                  modelMutationLocked ||
                  editQueue.status === "failed" ||
                  editQueue.status === "conflict"
                }
                onOpenHistory={() => setHistoryOpen(true)}
                onEditorDirtyChange={setXlmEditorDirty}
                actions={
                  !restrictedProductionUi ? (
                    <IconButton
                      tone={chatOpen ? "secondary" : "quiet"}
                      label={chatOpen ? "Close model chat" : "Ask model"}
                      tooltip={
                        chatOpen
                          ? "Close the model chat side peek."
                          : "Ask questions about this exact published model."
                      }
                      icon={<ChatIcon />}
                      aria-expanded={chatOpen}
                      aria-controls="agent-model-chat-sidepeek"
                      disabled={modelSavePending && !chatOpen}
                      onClick={() => setChatOpen(!chatOpen)}
                    />
                  ) : undefined
                }
                companyId={companyId}
                documents={sourceDocuments}
                reference={unavailableModelReference ? null : modelReference}
                inspectReference={inspectModelReference}
                onClearReference={clearModelReference}
                onOpenReference={openModelReference}
                onOpenReview={openReview}
              />
            ) : null}
          </>
        )}

        {activeTab === "documents" && (
          <AgentModelDocumentsPanel
            key={model.run_id}
            companyId={companyId}
            runId={model.run_id}
            model={snapshot}
            modelLoading={xlm.isPending && !snapshot}
            onOpenReference={(reference) => openModelReference(reference, true)}
          />
        )}

        {activeTab === "assumptions" &&
          (xlm.isPending && !snapshot ? (
            <Loading label="Loading assumptions review…" />
          ) : xlm.error && !snapshot ? (
            <ErrorState error={xlm.error} onRetry={() => xlm.refetch()} />
          ) : snapshot ? (
            <AgentAssumptionsReviewPanel
              companyId={companyId}
              model={snapshot}
              documents={sourceDocuments}
              activeRecordId={activeReviewRecordId}
              onOpenModelReference={(reference) =>
                openModelReference(reference, true)
              }
            />
          ) : null)}

        {activeTab === "guidance" &&
          (xlm.isPending && !snapshot ? (
            <Loading label="Loading guidance review…" />
          ) : xlm.error && !snapshot ? (
            <ErrorState error={xlm.error} onRetry={() => xlm.refetch()} />
          ) : snapshot ? (
            <AgentGuidanceReviewPanel
              companyId={companyId}
              model={snapshot}
              documents={sourceDocuments}
              activeRecordId={activeReviewRecordId}
              onOpenModelReference={(reference) =>
                openModelReference(reference, true)
              }
            />
          ) : null)}

        {activeTab === "run-details" && (
          <AgentRunDetails
            key={model.run_id}
            runId={model.run_id}
            showVisibilityControl={false}
          />
        )}

        {activeTab === "summary" && (
          <AgentSummaryPanel
            companyId={companyId}
            runId={model.run_id}
            versionId={versionId}
            model={snapshot}
            modelReferenceState={
              snapshot ? "ready" : xlm.error ? "error" : "loading"
            }
            onOpenModelReference={openModelReference}
          />
        )}
      </div>

      {chatOpen && (
        <AgentModelChatSidePeek
          key={versionId}
          companyId={companyId}
          versionId={versionId}
          isRegistered={auth.isRegistered}
          model={snapshot}
          modelReferenceState={
            snapshot ? "ready" : xlm.error ? "error" : "loading"
          }
          revisionLocked={modelSavePending}
          width={modelChatWidth}
          fullScreen={modelChatFullScreen}
          onClose={() => setChatOpen(false)}
          onResize={setModelChatWidth}
          onFullScreenChange={setModelChatFullScreen}
          onEditActivityChange={setChatEditActive}
          onOpenModelReference={(reference) => {
            setModelChatFullScreen(false);
            openModelReference(reference);
          }}
        />
      )}
    </div>
  );
}
